import { AIConfig, DEFAULT_AI_CONFIG, AI_CONFIG_VERSION as TYPES_AI_CONFIG_VERSION } from './types';
import { normalizeAIConfig } from './aiConfigModifiers';

/**
 * AIConfig のスキーマバージョン（types.ts から再エクスポート）
 *
 * - v1: Lv1-5 用パラメータのみ（13個）
 * - v2: Lv6 パラメータ追加（lookAheadDepth, opponentModeling, mixedStrategy, resourceOptimization）
 */
export const AI_CONFIG_VERSION = TYPES_AI_CONFIG_VERSION;

/**
 * バージョン情報を持った AIConfig（永続化形式）
 *
 * LocalStorage に保存するときは __version プロパティを付与する。
 * 読み込み時は migrateAIConfig() を通して最新版に揃える。
 */
export interface VersionedAIConfig extends AIConfig {
  __version?: number;
}

/**
 * 不明な形式の AIConfig を最新の AIConfig に変換する
 *
 * - null/undefined/プリミティブ/配列 → DEFAULT_AI_CONFIG
 * - v1 形式（__version なし）→ Lv6 パラメータをデフォルト補完
 * - v2 形式（__version === 2）→ クランプを通してそのまま返す
 *
 * 入力オブジェクトはミューテートしない（純粋関数）。
 */
export function migrateAIConfig(input: unknown): AIConfig {
  if (input === null || input === undefined) {
    return { ...DEFAULT_AI_CONFIG };
  }
  if (typeof input !== 'object') {
    return { ...DEFAULT_AI_CONFIG };
  }
  if (Array.isArray(input)) {
    return { ...DEFAULT_AI_CONFIG };
  }

  const obj = input as Record<string, unknown>;

  // バージョン番号の取得（なければ v1 とみなす）
  const version = typeof obj.__version === 'number' ? obj.__version : 1;

  // v1 → v2: Lv6 パラメータが欠落していればデフォルト値で補完
  // normalizeAIConfig が undefined をデフォルト値に変換するため、
  // ここでは明示的な補完は不要。Partial<AIConfig> として normalizeAIConfig に渡す。
  // ただし v1 形式と判定された場合の挙動を明示するためにバージョン分岐は残す。
  let candidate: Partial<AIConfig>;
  if (version < 2) {
    // v1 → v2 移行: 既存値を保持しつつ、Lv6 パラメータは undefined のまま
    // normalizeAIConfig がデフォルト値で補完する
    candidate = stripVersionField(obj);
  } else {
    // v2 以降: そのまま candidate として扱う
    candidate = stripVersionField(obj);
  }

  // 最終的に normalizeAIConfig でクランプとデフォルト適用を行う
  return normalizeAIConfig(candidate);
}

/**
 * __version プロパティを除外した Partial<AIConfig> を返すヘルパー
 */
function stripVersionField(obj: Record<string, unknown>): Partial<AIConfig> {
  const { __version: _ignored, ...rest } = obj;
  return rest as Partial<AIConfig>;
}

/**
 * 保存時に __version を付与する
 */
export function withVersion(config: AIConfig): VersionedAIConfig {
  return { ...config, __version: AI_CONFIG_VERSION };
}
